import axios from 'axios'
import { useSetAtom } from 'jotai'
import { type FC, type PropsWithChildren, Suspense, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { ChataiConversationDrawer } from '~/app/ChatAI/ChataiConversationDrawer'
import { ChataiConversationDrawerOpenButton } from '~/app/ChatAI/ChataiConversationDrawerOpenButton'
import type { AppLayoutBoxVariant } from '~/components/Layout/Box/AppLayoutBox'
import { ENDPOINT } from '~/config/api'
import { useAuthClaims } from '~/hooks/useAuthClaims'
import { signOut } from '~/lib/auth'
import { chataiDrawerOpenState } from '~/states/atoms/ChatAI'
import { layoutMainRefAtom } from '~/states/atoms/Layout'

import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  Alert,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Tooltip,
  colors,
  useMediaQuery,
} from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import { useTheme } from '@mui/material/styles'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { NavLink } from '../Navigation/Link/NavLink'
import { ColorModeMenu } from '../Surface/AppBar/ColorModeMenu'

type GuardAuthProps = {
  /**
   * レイアウトを含まない
   */
  disableLayout?: boolean

  /**
   * レイアウト Variant
   */
  layoutVariant?: AppLayoutBoxVariant

  /**
   * 管理権限が必要なページ
   */
  adminZone?: boolean

  /**
   * 代理店権限が必要なページ
   */
  agencyZone?: boolean

  /**
   * 認証されていない時にリダイレクトされるURL
   * Default: /login
   */
  redirect?: string
}

/**
 * GuardAuth
 *
 * React Router と同時に使用し、`認証済み`のアクセスのみ表示を許可させたいRouteのelementに挟み込みます。
 *
 * - `認証済み`とはAWS Cognitoとのセッションが有効な状態を指します。
 *
 * ----
 *
 * #### 記述例
 *
 * ```tsx
 * <Routes>
 *   <Route path="/" element={<GuardAuth children={<Home />} />} />
 *   <Route path="/secure" element={
 *     <GuardAuth redirect="/non-secure" children={<SecurePage />}
 *   />} />
 * </Routes>
 * ```
 */
export const GuardAuth: FC<PropsWithChildren<GuardAuthProps>> = ({
  children,
  layoutVariant = 'default',
  redirect = '/login',
  disableLayout = false,
  adminZone = false,
  agencyZone = false,
}) => {
  const theme = useTheme()
  const location = useLocation()

  const mainRef = useRef<HTMLDivElement | null>(null)
  const setLayoutMainRefState = useSetAtom(layoutMainRefAtom)

  const drawerOpen = useRecoilValue(chataiDrawerOpenState)

  const isSizeLg = useMediaQuery(theme.breakpoints.up('lg'))

  // Cognito のセッション。null なら未ログイン。
  const { claims, isChecking: isAuthPending } = useAuthClaims()
  const isAuthenticated = !!claims

  const queryClient = useQueryClient()
  const [isExecuting, setIsExecuting] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('info')

  // 実行回数を取得するuseQuery
  const { data: executionCountData } = useQuery({
    queryKey: ['batch-import-execution-count'],
    queryFn: async () => {
      // Authorization は lib/apiClient.ts の interceptor が毎回付ける。
      const response = await axios.get(`${ENDPOINT}/api/batch-import/execution-count`, {
        headers: { Accept: 'application/json' },
      })
      return response.data
    },
    enabled: isAuthenticated,
    retry: false,
  })

  // 進捗状況を取得するuseQuery
  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ['batch-import-progress'],
    queryFn: async () => {
      const response = await axios.get(`${ENDPOINT}/api/batch-import/progress`, {
        headers: { Accept: 'application/json' },
      })
      return response.data
    },
    enabled: showProgress && isAuthenticated,
    refetchInterval: 3000, // 3秒ごとに進捗を取得
    retry: false,
  })

  // 進捗状況の変化を監視
  useEffect(() => {
    if (progressData?.success && progressData.progress) {
      const progress = progressData.progress
      const percentage = progress.progress_percentage as number
      const status = progress.overall_status
      const statusLabel = progress.status_label

      if (status === 'processing') {
        setSnackbarMessage(
          `バッチ処理中: ${percentage.toFixed(0)}% (${progress.completed_tasks}/${progress.total_tasks})`
        )
        setSnackbarSeverity('info')
        setSnackbarOpen(true)
      } else if (status === 'completed') {
        setSnackbarMessage(`バッチ処理完了: ${percentage}% (成功率: ${progress.success_rate}%)`)
        setSnackbarOpen(true)
        setShowProgress(false)
        setIsExecuting(false)
        setSnackbarSeverity('success')
        // 実行回数データを更新
        void queryClient.invalidateQueries({ queryKey: ['batch-import-execution-count'] })
      } else if (status === 'failed') {
        setSnackbarMessage(`バッチ処理失敗: ${statusLabel} (失敗: ${progress.failed_tasks}件)`)
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setShowProgress(false)
        setIsExecuting(false)
        // 実行回数データを更新
        void queryClient.invalidateQueries({ queryKey: ['batch-import-execution-count'] })
      }
    }
  }, [progressData])

  const sleep = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  // バッチインポート実行
  const handleBatchImport = async () => {
    if (!isAuthenticated) return

    setIsExecuting(true)

    try {
      const response = await axios.post(
        `${ENDPOINT}/api/batch-import/execute`,
        {},
        { headers: { Accept: 'application/json' } }
      )

      if (response.data.success) {
        await sleep(500)
        setSnackbarMessage('バッチインポートを開始しました')
        setSnackbarSeverity('info')
        setSnackbarOpen(true)
        setShowProgress(true)
        // 実行回数データを更新
        void queryClient.invalidateQueries({ queryKey: ['batch-import-execution-count'] })
      } else {
        setSnackbarMessage(String(response.data.message) || 'バッチインポートの開始に失敗しました')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setShowProgress(false)
        setIsExecuting(false)
      }
    } catch (error: any) {
      let errorMessage = 'バッチインポートエラーが発生しました'
      if (error.response?.status === 429) {
        errorMessage = error.response.data.message || '本日の実行回数上限に達しています'
      }
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setShowProgress(false)
      setIsExecuting(false)
    }
  }

  // Snackbarを閉じる
  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  // ログアウト。react-query のキャッシュも捨てないと、
  // 別ユーザーでログインし直したときに前のユーザーのデータが一瞬見える。
  const handleSignOut = async () => {
    await signOut()
    queryClient.clear()
    window.location.assign('/login')
  }

  // 実行回数の情報を取得
  const executionCount = (executionCountData?.success ? executionCountData.execution_count : 0) as number
  const maxExecutions = (executionCountData?.success ? executionCountData.max_executions : 5) as number
  const canExecute = executionCountData?.success ? executionCountData.can_execute : true

  // ボタンのテキスト
  const getButtonText = () => {
    if (isExecuting) return '実行中...'
    return `データ更新実行（本日の実行回数：${executionCount}/${maxExecutions}）`
  }

  useEffect(() => {
    if (mainRef.current) setLayoutMainRefState(mainRef.current)
  }, [setLayoutMainRefState])

  // セッション確認中は中身を出さない（未ログイン扱いで一瞬リダイレクトされるのを防ぐ）。
  if (isAuthPending) return <LinearProgress />

  if (!isAuthenticated) {
    console.info(`[GuardAuth] redirect to ${redirect}`)
    return <Navigate to={redirect} state={{ from: location }} replace />
  }

  return (
    <>
      <MuiAppBar
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: '2px solid #20a3af',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
        }}>
        <Toolbar
          variant="regular"
          disableGutters
          sx={{ pl: 1, pr: 2, backgroundColor: `${theme.palette.background.paper} !important` }}>
          <Stack
            direction="row"
            spacing={0}
            sx={{
              alignItems: 'center',
              height: 44,
              pl: 1,
              pr: 2,
              boxSizing: 'border-box',
            }}>
            <NavLink
              href={'/'}
              sx={{
                display: 'flex',
                textDecoration: 'none',
                color: '#20a3af',
                alignItems: 'center',
                gap: 1.5,
              }}>
              <img src="/images/logo-partner.svg" alt="" style={{ height: 24, width: 'auto' }} />
              <CloseIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <img src="/images/logo-canaly.svg" alt="" style={{ height: 22, width: 'auto' }} />
              <span style={{ fontFamily: 'Archivo Black, sans-serif', fontSize: '16px', marginLeft: '10px' }}>
                DASHBOARD
              </span>
            </NavLink>
          </Stack>

          {/* {!disableProjectMenu && <AppProjectMenu height="100%" />} */}

          <Stack
            sx={{
              alignItems: 'center',
              ml: 'auto',
            }}
            direction="row"
            spacing={1}>
            <Tooltip
              title={
                canExecute
                  ? ''
                  : '実行回数の上限に達しました。データを更新しても値が合わない場合は、システム管理者にお問い合わせください。'
              }>
              <Paper>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => void handleBatchImport()}
                  disabled={isExecuting || !canExecute}
                  color={canExecute ? 'info' : 'inherit'}
                  sx={{
                    minWidth: 100,
                    justifyContent: 'flex-start',
                  }}>
                  {getButtonText()}
                </Button>
              </Paper>
            </Tooltip>

            {/* {!disableNotification && (
            <IconButton disabled>
              <Badge color="error" variant="dot" badgeContent={0}>
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          )} */}

            {/* <AvatarMenu /> */}
            <ColorModeMenu />
            <Chip label={claims?.email ?? ''} color="default" size="small" />
            <Tooltip title="ログアウト">
              <IconButton size="small" onClick={() => void handleSignOut()}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </MuiAppBar>
      <Suspense fallback={<LinearProgress />}>
        <Box
          id="smc-app-main"
          sx={{
            mt: 5,
            width: drawerOpen && isSizeLg ? 'calc(100% - 230px)' : '100%',
            ml: drawerOpen && isSizeLg ? '230px' : 0,

            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),

            '& > *:first-of-type': {
              borderTopWidth: 1,
              borderTopStyle: 'solid',
              borderTopColor: theme.palette.border.light,
            },

            '.content': {
              ol: {
                display: 'flex',
                flexDirection: 'column',
                pl: '1rem',
              },
              'ol:first-of-type': {
                mt: 0,
              },
              li: {
                pl: '6px',
              },
            },

            '.conversation-bottom-spacer, .empty-conversation, .conversation-progress': {
              border: 'none',
            },

            // #root の zoom: 0.9 を打ち消し、ビューポートを埋める（下の余白防止）
            minHeight: 'calc(100vh / 0.9)',

            background: theme.palette.mode === 'dark' ? theme.palette.background.default : colors.blueGrey[50],
          }}>
          <ChataiConversationDrawer />
          {/* ドロワー開閉ボタン */}
          {!drawerOpen && isSizeLg && <ChataiConversationDrawerOpenButton anchor="lefttop" />}
          {children}
        </Box>
      </Suspense>
      {/* 進捗状況表示用のSnackbar */}
      <Snackbar
        open={snackbarOpen}
        // autoHideDuration={snackbarSeverity === 'info' ? null : 6000}
        // onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 4, // AppBarの高さ分を避けて下にずらす
        }}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', minWidth: 300 }}
          variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
  // skip: not authorization
  // if (isAuthenticated === null) {
  //   return <>test</>
  // }

  // !isAuthenticated && console.info(`[GuardAuth] redirect to ${redirect}`)

  // return isAuthenticated ? (
  //   disableLayout ? (
  //     <Suspense fallback={<LinearProgress />}>
  //       <AuthViewerQuery />
  //       <AuthRoot adminZone={adminZone} agencyZone={agencyZone}>
  //         {children}
  //       </AuthRoot>
  //     </Suspense>
  //   ) : (
  //     <AppLayoutBox variant={layoutVariant}>
  //       <AuthViewerQuery />
  //       <Suspense
  //         fallback={
  //           <>
  //             <LinearProgress
  //               sx={{
  //                 position: 'absolute',
  //                 top: 0,
  //                 left: 0,
  //                 right: 0,
  //                 height: 3,
  //                 zIndex: theme.zIndex.tooltip,
  //               }}
  //             />
  //             <Box />
  //           </>
  //         }>
  //         <AuthRoot adminZone={adminZone} agencyZone={agencyZone}>
  //           {children}
  //         </AuthRoot>
  //       </Suspense>
  //     </AppLayoutBox>
  //   )
  // ) : (
  //   <Navigate to={redirect} state={{ from: location }} replace />
  // )
}
